import { useState, useEffect, useCallback, memo } from "react";
import Button from "./Button";
import { RxCross2 } from "react-icons/rx";

const DateRangePicker = memo(({ onChange, submitMode = false }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState(false);

  const handleApply = useCallback(() => {
    if (from && to) {
      onChange({ from, to });
      setApplied(true);
    }
  }, [from, to, onChange]);

  const handleClear = useCallback(() => {
    setFrom("");
    setTo("");
    setApplied(false);
    onChange({ from: "", to: "" });
  }, [onChange]);

  // Auto-fetch mode: fires onChange as soon as both dates are set
  useEffect(() => {
    if (!submitMode && from && to) {
      onChange({ from, to });
    }
  }, [from, to, submitMode]);

  // Reset applied state whenever dates change in submit mode
  useEffect(() => {
    if (submitMode) {
      setApplied(false);
    }
  }, [from, to, submitMode]);

  const isReady = from && to;

  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={from}
        max={to || undefined}
        onChange={(e) => setFrom(e.target.value)}
        className="border border-mediumgray rounded-md px-3 py-2 text-sm text-darkergray focus:outline-none focus:ring-1 focus:ring-darkgray"
      />
      <span className="text-sm text-darkgray">to</span>
      <input
        type="date"
        value={to}
        min={from || undefined}
        onChange={(e) => setTo(e.target.value)}
        className="border border-mediumgray rounded-md px-3 py-2 text-sm text-darkergray focus:outline-none focus:ring-1 focus:ring-darkgray"
      />

      {/* Apply button — only shown in submitMode */}
      {submitMode && (
        <Button
          onClick={handleApply}
          disabled={!isReady || applied}
          title={
            !isReady
              ? "Select both start and end dates first"
              : applied
                ? "Filter already applied"
                : "Apply date filter"
          }
          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
            ${
              isReady && !applied
                ? "bg-darkergray text-white hover:bg-black"
                : "bg-lightgray text-darkgray cursor-not-allowed opacity-50"
            }`}
        >
          {applied ? "Applied" : "Apply"}
        </Button>
      )}

      {/* Clear — only shown when a date is selected */}
      {(from || to) && (
        <Button
          onClick={handleClear}
          title="Clear date range"
          className="p-2 rounded-full hover:bg-mediumgray text-darkergray transition-colors"
        >
          <RxCross2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
});

export default DateRangePicker;
